import { CardWithHeader } from "@/components/card-with-header";
import { ComplianceRequirementCard } from "@/components/compliance/requirement-card";
import { RefreshIconButton } from "@/components/refresh-icon-button";
import { CaseWorkflowTimeline } from "@/components/compliance/case-workflow-timeline";
import { UploadDocumentDialog } from "@/components/compliance/upload-document-dialog";
import { Spinner } from "@/components/ui/spinner";
import { useAnalytics } from "@/lib/analytics";
import { complianceApi } from "@/lib/compliance-api";
import { complianceQueryKeys } from "@/lib/compliance-query-keys";
import { useMerchantComplianceCase } from "@/hooks/useMerchantComplianceCase";
import { formatTransactionVolumeWithCurrency } from "@/components/compliance/admin-case-detail.utils";
import { sortRequirements } from "@/components/compliance/compliance-case-detail.utils";
import type {
  ComplianceRequirement,
  ComplianceUpload,
  MerchantSupportingInfo,
} from "@/types/compliance";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Send, Undo2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { MerchantSupportingInfoTextDisplay } from "@/components/compliance/merchant-supporting-info-text-display";
import { PaymentLinksPanel } from "@/components/compliance/payment-links-panel";
import { SubmitForReviewDialog } from "@/components/compliance/submit-for-review-dialog";
import { toast } from "sonner";
import { useCanSeeTmBackedComplianceScreen } from "./-components/is-compliance-enabled";
import { MerchantTransactionMonitoringCaseRoute } from "../_transaction-monitoring/-components/merchant-case-detail-route";

export const Route = createFileRoute(
  "/(app)/_main-layout/_compliance/compliance_/$caseId",
)({
  component: MerchantComplianceCaseDetail,
});

function MerchantComplianceCaseDetail() {
  const { caseId } = Route.useParams();
  const tmBackedCompliance = useCanSeeTmBackedComplianceScreen();
  if (tmBackedCompliance) {
    return <MerchantTransactionMonitoringCaseRoute caseId={caseId} />;
  }

  return <FxMerchantComplianceCaseDetail caseId={caseId} />;
}

async function downloadFileFromUrl({
  url,
  fileName,
}: {
  url: string;
  fileName: string;
}) {
  const response = await fetch(url);
  if (!response.ok) throw new Error("Failed to download file");
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function FxMerchantComplianceCaseDetail({
  caseId,
}: {
  readonly caseId: string;
}) {
  const context = Route.useRouteContext();
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const [selectedRequirementId, setSelectedRequirementId] = useState<
    string | null
  >(null);
  const [textDraftByRequirementId, setTextDraftByRequirementId] = useState<
    Record<string, string>
  >({});
  const [submitForReviewDialogOpen, setSubmitForReviewDialogOpen] =
    useState(false);

  const caseQuery = useMerchantComplianceCase({
    accessToken: context.accessToken,
    applicationId: context.selectedApplicationId,
    caseId,
  });

  const sortedRequirements = caseQuery.data?.requirements
    ? sortRequirements(caseQuery.data.requirements)
    : [];

  const invalidateComplianceQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: complianceQueryKeys.merchantCase(caseId),
    });
    void queryClient.invalidateQueries({
      queryKey: ["compliance", "merchant", "cases"],
    });
    void queryClient.invalidateQueries({
      queryKey: complianceQueryKeys.merchantRequiringActionCount(
        context.selectedApplicationId,
      ),
    });
  }, [caseId, context.selectedApplicationId, queryClient]);

  const _handleOpenUploadDialog = useCallback(
    (requirement: ComplianceRequirement) => {
      analytics.track("compliance_upload_dialog_opened", {
        case_id: caseId,
        requirement_id: requirement.id,
        collection_type: requirement.collectionType,
      });
      setSelectedRequirementId(requirement.id);
    },
    [analytics, caseId],
  );

  const getUploadPreviewUrl = useCallback(
    async (upload: ComplianceUpload) => {
      const { url } = await complianceApi.merchant.getUploadDownloadUrl({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        uploadId: upload.id,
      });
      return url;
    },
    [context.accessToken, context.selectedApplicationId],
  );

  const deleteUploadMutation = useMutation({
    mutationFn: async (upload: ComplianceUpload) => {
      await complianceApi.merchant.deleteUpload({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        uploadId: upload.id,
      });
      return upload;
    },
    onSuccess: (upload) => {
      analytics.track("compliance_upload_deleted", {
        case_id: caseId,
        requirement_id: upload.requirementId,
      });
      toast.success(`${upload.originalFileName} deleted`);
      invalidateComplianceQueries();
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to delete");
    },
  });

  const handleDownloadUpload = useCallback(
    async (upload: ComplianceUpload) => {
      try {
        const url = await getUploadPreviewUrl(upload);
        await downloadFileFromUrl({ url, fileName: upload.originalFileName });
        analytics.track("compliance_document_downloaded", {
          case_id: caseId,
          requirement_id: upload.requirementId,
        });
      } catch (error) {
        toast.error(
          error instanceof Error ? error.message : "Failed to download file",
        );
      }
    },
    [caseId, getUploadPreviewUrl, analytics],
  );

  const uploadRequirementFile = useCallback(
    async ({ requirementId, file }: { requirementId: string; file: File }) => {
      if (file.size > complianceApi.maxUploadSizeBytes) {
        throw new Error("File exceeds 50MB limit");
      }

      const presign = await complianceApi.merchant.presignUpload({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        requirementId,
        originalFileName: file.name,
        contentType: file.type || "text/plain",
        sizeBytes: file.size,
      });

      const uploadResponse = await fetch(presign.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "text/plain",
          ...presign.headers,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload document to storage");
      }

      await complianceApi.merchant.completeUpload({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        requirementId,
        uploadToken: presign.uploadToken,
      });
    },
    [context.accessToken, context.selectedApplicationId],
  );

  const submitRequirementMutation = useMutation({
    mutationFn: async ({
      requirement,
      textDraft,
    }: {
      requirement: ComplianceRequirement;
      textDraft: string;
    }) => {
      if (requirement.collectionType === "text") {
        const trimmed = textDraft.trim();
        if (!trimmed) {
          throw new Error("Please enter text before submitting");
        }

        const file = new File([trimmed], `${requirement.requirementCode}.txt`, {
          type: "text/plain",
        });
        await uploadRequirementFile({ requirementId: requirement.id, file });
      }

      await complianceApi.merchant.submitRequirement({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        requirementId: requirement.id,
      });

      return requirement;
    },
    onSuccess: (requirement) => {
      analytics.track("compliance_requirement_submitted", {
        case_id: caseId,
        requirement_id: requirement.id,
        collection_type: requirement.collectionType,
      });
      toast.success("Requirement submitted for review");
      if (requirement.collectionType === "text") {
        setTextDraftByRequirementId((current) => {
          return Object.fromEntries(
            Object.entries(current).filter(
              ([requirementId]) => requirementId !== requirement.id,
            ),
          );
        });
      }
      invalidateComplianceQueries();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to submit requirement",
      );
    },
  });

  const deleteSupportingInfoMutation = useMutation({
    mutationFn: (uploadId: string) =>
      complianceApi.merchant.deleteSupportingInfo({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        caseId,
        uploadId,
      }),
    onSuccess: () => {
      toast.success("Supporting information removed");
      invalidateComplianceQueries();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to remove file",
      );
    },
  });

  const getSupportingInfoDownloadUrl = useCallback(
    async (item: MerchantSupportingInfo) => {
      const { url } = await complianceApi.merchant.getSupportingInfoDownloadUrl(
        {
          accessToken: context.accessToken,
          applicationId: context.selectedApplicationId,
          caseId,
          uploadId: item.uploadId,
        },
      );
      return url;
    },
    [context.accessToken, context.selectedApplicationId, caseId],
  );

  const unsubmitForReviewMutation = useMutation({
    mutationFn: () =>
      complianceApi.merchant.unsubmitForReview({
        accessToken: context.accessToken,
        applicationId: context.selectedApplicationId,
        caseId,
      }),
    onSuccess: () => {
      analytics.track("compliance_case_unsubmitted", {
        case_id: caseId,
      });
      toast.success("Case withdrawn from review");
      invalidateComplianceQueries();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to withdraw case",
      );
    },
  });

  const workflowStatus = caseQuery.data?.workflowStatus;

  const canEdit =
    workflowStatus !== "PENDING_VOLUME_REVIEW" &&
    workflowStatus !== "APPROVED" &&
    caseQuery.data?.status !== "COMPLETED";

  // Requirement counts for summary bar — workflow-aware
  let actionNeededCount = 0;
  let underReviewCount = 0;
  let approvedCount = 0;

  for (const r of sortedRequirements) {
    if (r.status === "APPROVED") {
      approvedCount += 1;
    } else if (r.status === "SUBMITTED") {
      underReviewCount += 1;
    } else if (
      r.status === "REQUIRED" &&
      workflowStatus === "PENDING_VOLUME_REVIEW"
    ) {
      // Submitted without evidence — still under review
      underReviewCount += 1;
    } else if (r.status === "REQUIRED" && workflowStatus === "APPROVED") {
      // Approved via case
      approvedCount += 1;
    } else {
      // REQUIRED (normal) or REJECTED
      actionNeededCount += 1;
    }
  }

  const renderRequirementCard = (requirement: ComplianceRequirement) => (
    <ComplianceRequirementCard
      key={requirement.id}
      requirement={requirement}
      caseWorkflowStatus={workflowStatus}
      canUpload={canEdit}
      onUpload={() => setSelectedRequirementId(requirement.id)}
      onDownloadUpload={(upload) => void handleDownloadUpload(upload)}
      onRequestPreviewUrl={getUploadPreviewUrl}
      onDeleteUpload={(upload) => {
        deleteUploadMutation.mutate(upload);
      }}
      deletingUploadId={deleteUploadMutation.variables?.id}
      onSubmitRequirement={() => {
        submitRequirementMutation.mutate({
          requirement,
          textDraft: textDraftByRequirementId[requirement.id] ?? "",
        });
      }}
      isSubmittingRequirement={
        submitRequirementMutation.isPending &&
        submitRequirementMutation.variables?.requirement.id === requirement.id
      }
      textDraft={textDraftByRequirementId[requirement.id] ?? ""}
      onTextDraftChange={(value) => {
        setTextDraftByRequirementId((current) => ({
          ...current,
          [requirement.id]: value,
        }));
      }}
    />
  );

  return (
    <div className="space-y-3">
      <div>
        <Link
          to="/compliance"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-volume-text-purple hover:underline"
        >
          <ArrowLeft className="size-4" />
          Back to all cases
        </Link>
      </div>

      <CardWithHeader
        variant="exchange"
        label="Compliance"
        title="Case Details"
        headerContent={
          <RefreshIconButton
            label="Refresh case details"
            onRefresh={() => {
              analytics.track("compliance_case_detail_refreshed", {
                case_id: caseId,
              });
              void caseQuery.refetch();
            }}
          />
        }
      >
        {caseQuery.isLoading ? (
          <div className="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
            <Spinner width={20} height={20} />
            <span>Loading case details...</span>
          </div>
        ) : null}

        {caseQuery.data ? (
          <div className="space-y-5">
            {/* Header: customer + status */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-lg font-semibold text-pretty text-slate-900">
                  {caseQuery.data.customerName || "Unknown customer"}
                </h1>
                {caseQuery.data.transactionVolume != null ||
                caseQuery.data.transactionCount != null ? (
                  <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    {caseQuery.data.transactionVolume != null &&
                    caseQuery.data.transactionVolumeCurrency != null ? (
                      <div>
                        <dt className="sr-only">Transaction volume</dt>
                        <dd className="font-medium text-slate-700">
                          {formatTransactionVolumeWithCurrency(
                            caseQuery.data.transactionVolume,
                            caseQuery.data.transactionVolumeCurrency,
                          )}
                        </dd>
                      </div>
                    ) : null}
                    {caseQuery.data.transactionCount != null ? (
                      <div>
                        <dt className="sr-only">Transaction count</dt>
                        <dd className="font-medium text-slate-700">
                          {caseQuery.data.transactionCount} transactions
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {workflowStatus === "WAITING_FOR_INPUT" ||
                workflowStatus === "PARTIAL" ? (
                  <Button
                    size="sm"
                    onClick={() => setSubmitForReviewDialogOpen(true)}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                  >
                    <Send className="mr-1.5 size-4" />
                    Submit case for review
                  </Button>
                ) : null}
                {workflowStatus === "PENDING_VOLUME_REVIEW" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => unsubmitForReviewMutation.mutate()}
                    disabled={unsubmitForReviewMutation.isPending}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                  >
                    {unsubmitForReviewMutation.isPending ? (
                      <span className="mr-1.5 inline-flex">
                        <Spinner width={14} height={14} />
                      </span>
                    ) : (
                      <Undo2 className="mr-1.5 size-4" />
                    )}
                    Cancel submission
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Workflow timeline */}
            {workflowStatus ? (
              <CaseWorkflowTimeline workflowStatus={workflowStatus} />
            ) : null}

            <PaymentLinksPanel
              paymentIds={caseQuery.data.paymentIds}
              variant="merchant"
              accessToken={context.accessToken}
              applicationId={context.selectedApplicationId}
            />

            {/* Requirements */}
            {caseQuery.data.requirements.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-600">
                No requirements found for this case.
              </div>
            ) : null}

            {sortedRequirements.length > 0 ? (
              <div className="space-y-3">
                {/* Summary pills */}
                <div className="flex flex-wrap items-center gap-2">
                  {actionNeededCount > 0 ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                      <span className="font-bold">{actionNeededCount}</span>
                      Action required
                    </span>
                  ) : null}
                  {underReviewCount > 0 ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                      <span className="font-bold">{underReviewCount}</span>
                      Under review
                    </span>
                  ) : null}
                  {approvedCount > 0 ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                      <span className="font-bold">{approvedCount}</span>
                      Approved
                    </span>
                  ) : null}
                </div>

                {/* Flat requirement list */}
                <div className="space-y-2">
                  {sortedRequirements.map(renderRequirementCard)}
                </div>
              </div>
            ) : null}

            {/* Supporting information (below requirements) */}
            {(caseQuery.data.merchantSupportingInformation?.length ?? 0) > 0 ? (
              <div className="space-y-2">
                <h2 className="text-sm font-medium text-slate-700">
                  Supporting information you submitted
                </h2>
                <MerchantSupportingInfoTextDisplay
                  items={caseQuery.data.merchantSupportingInformation}
                  onRequestDownloadUrl={getSupportingInfoDownloadUrl}
                  onDelete={
                    workflowStatus !== "APPROVED" &&
                    caseQuery.data.status !== "COMPLETED"
                      ? (item) =>
                          deleteSupportingInfoMutation.mutate(item.uploadId)
                      : undefined
                  }
                  deletingUploadId={
                    deleteSupportingInfoMutation.isPending
                      ? (deleteSupportingInfoMutation.variables ?? null)
                      : null
                  }
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {selectedRequirementId ? (
          <UploadDocumentDialog
            open={Boolean(selectedRequirementId)}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedRequirementId(null);
              }
            }}
            requirementId={selectedRequirementId}
            requirementTitle={
              caseQuery.data?.requirements.find(
                (r) => r.id === selectedRequirementId,
              )?.displayName
            }
            accessToken={context.accessToken}
            applicationId={context.selectedApplicationId}
            collectionType={
              caseQuery.data?.requirements.find(
                (r) => r.id === selectedRequirementId,
              )?.collectionType
            }
            onCompleted={() => {
              invalidateComplianceQueries();
            }}
          />
        ) : null}

        <SubmitForReviewDialog
          open={submitForReviewDialogOpen}
          onOpenChange={setSubmitForReviewDialogOpen}
          caseId={caseId}
          accessToken={context.accessToken}
          applicationId={context.selectedApplicationId}
          hasRequirementsWithoutEvidence={actionNeededCount > 0}
          onSuccess={() => {
            analytics.track("compliance_case_submitted_for_review", {
              case_id: caseId,
            });
            toast.success("Case submitted for review");
            invalidateComplianceQueries();
          }}
        />
      </CardWithHeader>
    </div>
  );
}
